/**
 * B-098 search-wall surfaces.
 *
 * Rendered by ComparisonStream when the api returns a terminal wall
 * reason in `empty_reason` instead of results (the wall fires in the
 * search route's early gate, before any Bright Data / LLM spend):
 *
 *   wall_signup_required -> SignupWallCard   (anon used 5 free searches)
 *   wall_daily_limit     -> DailyLimitCard   (signed-in used 30 today)
 *   wall_at_capacity     -> AtCapacityCard   (global 500/day cost cap)
 *
 * Design matches the approved mockups (dark card on #0a0a0b, amber
 * accents). No SearchAgainBar here: re-submitting would just re-wall.
 *
 * Auth CTA uses plain /sign-up and /sign-in page links (the app's
 * "fallback surface" per SiteHeader). It deliberately does NOT mount the
 * MioAuthDialog modal here: that modal pulls Clerk's useSignIn/useSignUp
 * hooks into ComparisonStream's client bundle, which threw an SSR
 * "invalid hook call" (useContext on a null dispatcher) during the
 * comparison page server-render. Links carry no React-context dependency,
 * so they render cleanly on the server and work whether or not Clerk is
 * configured.
 */

"use client";

import Link from "next/link";
import type { Route } from "next";

import { OctopusGlyph } from "@/components/OctopusGlyph";

const PRIMARY_BTN =
  "mt-7 inline-flex w-full max-w-[280px] items-center justify-center gap-2 " +
  "rounded-xl bg-amber-400 px-6 py-3 text-base font-bold text-[#1a1408] " +
  "transition hover:bg-amber-300";

function WallShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="cmp-surface relative mx-auto flex min-h-screen max-w-4xl items-center justify-center bg-background px-5 py-16 sm:px-8">
      <div className="w-full max-w-md rounded-3xl border border-border/60 bg-secondary px-8 py-10 text-center shadow-[0_8px_40px_rgba(0,0,0,0.45)]">
        {children}
      </div>
    </main>
  );
}

/** Amber line-art clock: resets-at-midnight motif (matches the mockup). */
function ClockIcon() {
  return (
    <svg
      width="50"
      height="50"
      viewBox="0 0 50 50"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="25" cy="25" r="16" stroke="#fbbf24" strokeWidth="2.6" />
      <line
        x1="25"
        y1="25"
        x2="25"
        y2="14"
        stroke="#fbbf24"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <line
        x1="25"
        y1="25"
        x2="32"
        y2="28"
        stroke="#fbbf24"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <circle cx="25" cy="25" r="1.9" fill="#fbbf24" />
    </svg>
  );
}

/** Anonymous visitor used their free searches -> sign up to continue. */
export function SignupWallCard() {
  return (
    <WallShell>
      <div className="mx-auto mb-6 flex h-[72px] w-[72px] items-center justify-center rounded-[17px] bg-amber-400">
        <OctopusGlyph size={44} />
      </div>
      <h1 className="text-[26px] font-bold leading-tight tracking-tight text-foreground">
        Create a free account to keep searching
      </h1>
      <p className="mx-auto mt-4 max-w-sm text-[15px] leading-relaxed text-muted-foreground">
        You&apos;ve used your 5 free searches. Sign up free to keep searching
        and beat the price.
      </p>

      <Link href={"/sign-up" as Route} className={PRIMARY_BTN}>
        Sign up free →
      </Link>

      <p className="mt-4 text-sm text-muted-foreground">
        Already have an account?{" "}
        <Link
          href={"/sign-in" as Route}
          className="font-semibold text-amber-300 transition hover:text-amber-200"
        >
          Sign in
        </Link>
      </p>

      <p className="mt-6 text-[13px] text-muted-foreground">
        Free forever. No card needed.
      </p>
    </WallShell>
  );
}

/** Visitor outside Australia tried to run a fresh search. Browsing saved
 * comparison pages stays open to everyone; only the paid fresh search is
 * gated (the backend stops it before any Bright Data / LLM spend). */
export function GeoBlockCard() {
  return (
    <WallShell>
      <div className="mx-auto mb-6 flex h-[72px] w-[72px] items-center justify-center rounded-[17px] bg-amber-400">
        <OctopusGlyph size={44} />
      </div>
      <h1 className="text-[25px] font-bold leading-tight tracking-tight text-foreground">
        Search is Australia only for now
      </h1>
      <p className="mx-auto mt-4 max-w-sm text-[15px] leading-relaxed text-muted-foreground">
        We only cover Australian stores at the moment. You&apos;re very welcome
        to browse the comparisons we&apos;ve already put together.
      </p>
      <Link href={"/browse" as Route} className={PRIMARY_BTN}>
        Browse comparisons →
      </Link>
    </WallShell>
  );
}

/** Signed-in user hit their daily allowance -> resets at midnight. */
export function DailyLimitCard() {
  return (
    <WallShell>
      <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-amber-400/10">
        <ClockIcon />
      </div>
      <h1 className="text-[25px] font-bold leading-tight tracking-tight text-foreground">
        You&apos;ve used today&apos;s searches
      </h1>
      <p className="mx-auto mt-4 max-w-sm text-[15px] leading-relaxed text-muted-foreground">
        All 30 of your daily searches are used. Fresh searches unlock again at
        midnight.
      </p>
      <p className="mt-5 text-[13px] text-muted-foreground">
        Saved and shared comparison pages still open anytime.
      </p>
      <Link href={"/browse" as Route} className={PRIMARY_BTN}>
        Browse comparisons →
      </Link>
    </WallShell>
  );
}

/** Global daily cost cap reached -> soft "busy" surface for everyone. */
export function AtCapacityCard() {
  return (
    <WallShell>
      <div className="mx-auto mb-6 flex h-[72px] w-[72px] items-center justify-center rounded-[17px] bg-amber-400">
        <OctopusGlyph size={44} />
      </div>
      <h1 className="text-[25px] font-bold leading-tight tracking-tight text-foreground">
        Octopusden is in high demand right now
      </h1>
      <p className="mx-auto mt-4 max-w-sm text-[15px] leading-relaxed text-muted-foreground">
        We&apos;re handling a lot of searches at the moment. Please try again a
        little later.
      </p>
      <p className="mt-5 text-[13px] text-muted-foreground">
        Saved and shared comparison pages still open anytime.
      </p>
      <Link href={"/browse" as Route} className={PRIMARY_BTN}>
        Browse comparisons →
      </Link>
    </WallShell>
  );
}
